import { useState } from 'react'
import { Box, Stack, SvgIcon, Typography } from '@mui/material'
import PhonelinkLockOutlinedIcon from '@mui/icons-material/PhonelinkLockOutlined'
import { useTranslation } from 'react-i18next'

import BannerAlert from '@hapana/shared/web/components/shared/banner-alert/banner-alert.component'
import { SEVERITY } from '@hapana/shared/web/components/shared/banner-alert/banner-alert.constants'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import { BUTTON_VARIANTS } from '@hapana/shared/web/components/shared/button/button.constants'
import Skeleton from '@hapana/shared/web/components/shared/skeleton/skeleton.component'
import { SKELETON_VARIANTS } from '@hapana/shared/web/components/shared/skeleton/skeleton.constants'

import { useMyApprovalPinStatus } from '_hooks/policy-compliance.hooks'
import { hasStaffPasswordSession } from '_services/pending-items-desk-unlock.service'

import {
  DESK_PIN_COPY_VALUES,
  DeskPinAvailability,
  DeskUnlockMethod,
  resolveDeskPinAvailability,
  resolveDeskUnlockMethod,
} from '../desk-unlock.utils'
import { PENDING_ITEMS_IDS, buildPendingItemsKey } from '../pending-items.constants'
import { styles } from '../pending-items.styles'
import { DeskPasswordUnlockForm, DeskPinUnlockForm } from './desk-unlock-forms.component'

const SIGN_IN_NOTICE: Partial<Record<DeskPinAvailability, string>> = {
  [DeskPinAvailability.LOCKED]: 'desk.pinLockedNotice',
  [DeskPinAvailability.NOT_SET]: 'desk.pinNotSetNotice',
}

type DeskUnlockProps = {
  onUnlocked: () => void
}

export const DeskUnlock = ({ onUnlocked }: DeskUnlockProps) => {
  const { t } = useTranslation('core')
  const [chosen, setChosen] = useState<DeskUnlockMethod | null>(null)
  const status = useMyApprovalPinStatus(true)

  if (status.isLoading) {
    return (
      <Stack spacing={2} aria-busy data-testid={PENDING_ITEMS_IDS.deskUnlockLoading}>
        <Skeleton variant={SKELETON_VARIANTS.rectangular} sx={styles.skeleton} />
        <Skeleton variant={SKELETON_VARIANTS.rectangular} sx={styles.skeleton} />
      </Stack>
    )
  }

  const availability = resolveDeskPinAvailability(status.data, new Date())
  const method = resolveDeskUnlockMethod(availability, chosen)
  const notice =
    method === DeskUnlockMethod.STAFF_SIGN_IN ? SIGN_IN_NOTICE[availability] : undefined

  return (
    <Box sx={styles.stack}>
      {notice ? (
        <Box data-testid={PENDING_ITEMS_IDS.deskPinNotice}>
          <BannerAlert
            severity={SEVERITY.INFO}
            message={t(buildPendingItemsKey(notice), DESK_PIN_COPY_VALUES)}
          />
        </Box>
      ) : null}
      {method === DeskUnlockMethod.PIN ? (
        <DeskPinUnlockForm onUnlocked={onUnlocked} />
      ) : (
        <DeskPasswordUnlockForm onUnlocked={onUnlocked} />
      )}
      {availability === DeskPinAvailability.READY ? (
        <Box>
          <Button
            variant={BUTTON_VARIANTS.TEXT}
            label={t(
              buildPendingItemsKey(
                method === DeskUnlockMethod.PIN ? 'desk.switchToSignIn' : 'desk.switchToPin'
              )
            )}
            onClick={() =>
              setChosen(
                method === DeskUnlockMethod.PIN
                  ? DeskUnlockMethod.STAFF_SIGN_IN
                  : DeskUnlockMethod.PIN
              )
            }
            data-testid={PENDING_ITEMS_IDS.deskSwitchMethod}
          />
        </Box>
      ) : null}
    </Box>
  )
}

type DeskLockScreenProps = {
  allowsStaffReturn: boolean
  onUnlocked: () => void
  onSignIn: () => void
}

const DeskLockScreen = ({ allowsStaffReturn, onUnlocked, onSignIn }: DeskLockScreenProps) => {
  const { t } = useTranslation('core')
  const canReauthenticate = hasStaffPasswordSession()

  return (
    <Box component="main" sx={styles.page}>
      <Box sx={styles.lockedCard} data-testid={PENDING_ITEMS_IDS.deskLocked}>
        <Box sx={styles.stateWrapper}>
          <SvgIcon component={PhonelinkLockOutlinedIcon} sx={styles.stateIcon} aria-hidden />
          <Typography variant="header2" component="h1" color="gray.80">
            {t(buildPendingItemsKey('desk.lockedTitle'))}
          </Typography>
          <Typography variant="medium" sx={styles.muted}>
            {t(
              buildPendingItemsKey(allowsStaffReturn ? 'desk.lockedBody' : 'desk.kioskLockedBody')
            )}
          </Typography>
        </Box>
        {allowsStaffReturn && canReauthenticate ? <DeskUnlock onUnlocked={onUnlocked} /> : null}
        {allowsStaffReturn && !canReauthenticate ? (
          <Box sx={styles.stack}>
            <Typography variant="medium" sx={styles.muted}>
              {t(buildPendingItemsKey('desk.noStaffSession'))}
            </Typography>
            <Button
              variant={BUTTON_VARIANTS.CONTAINED}
              label={t(buildPendingItemsKey('desk.signIn'))}
              onClick={onSignIn}
              data-testid={PENDING_ITEMS_IDS.deskSignIn}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}

export default DeskLockScreen
